import { Component, Input, Output, EventEmitter, OnInit, forwardRef, OnChanges, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ControlValueAccessor, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule, MatFormFieldAppearance } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { MsalService } from '@azure/msal-angular';

export interface AssignableAdmin {
  id: string;
  name: string;
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'cfi-assigned-admin-dropdown',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatFormFieldModule, MatSelectModule, NgxMatSelectSearchModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AssignedAdminDropdownComponent),
      multi: true
    }
  ],
  templateUrl: './assigned-admin-dropdown.component.html',
  styleUrls: ['./assigned-admin-dropdown.component.scss']
})
export class AssignedAdminDropdownComponent implements OnInit, OnChanges, ControlValueAccessor {
  @Input() assignableAdmins: AssignableAdmin[] = [];
  @Input() appearance: MatFormFieldAppearance = 'fill';
  @Input() placeholder: string = 'Search or select an admin';
  @Input() fieldClass: string = '';
  @Input() disabled: boolean = false;
  @Input() showSearch: boolean = true;
  @Input() showAllOption: boolean = false;
  @Input() allOptionLabel: string = 'All';
  @Input() showUnassignedOption: boolean = true;
  @Input() showAssignedOption: boolean = false;
  @Input() externalSearchControl?: FormControl;
  @Input() hasError: boolean = false;
  @Input() searchPlaceholder: string = 'Search names...';
  @Input() noEntriesFoundLabel: string = 'No matching names found';
  @Input() dataPw: string = 'assigned-admin-filter';
  @Input() searchDataPw: string = 'assigned-admin-search';

  @Output() adminChange = new EventEmitter<string | null>();
  @Output() opened = new EventEmitter<boolean>();

  searchControl = new FormControl('');
  filteredAdmins: AssignableAdmin[] = [];
  value: string | null = null;

  private msalService = inject(MsalService);

  // ControlValueAccessor implementation
  private onChange = (value: string | null): void => { void value; };
  private onTouched = () => {};

  ngOnInit() {
    this.filteredAdmins = this.getSortedAdmins();

    if (this.showSearch) {
      const searchControl = this.externalSearchControl || this.searchControl;
      searchControl.valueChanges.subscribe(() => {
        this.filterAdmins();
      });
    }
  }

  ngOnChanges() {
    this.filteredAdmins = this.getSortedAdmins();
    if (this.showSearch) {
      this.filterAdmins();
    }
  }

  private getSortedAdmins(): AssignableAdmin[] {
    try {
      const account = this.msalService.instance.getActiveAccount() || this.msalService.instance.getAllAccounts()[0];
      if (account) {
        const currentUserName = account.name || account.username || '';
        const currentUserId = account.localAccountId || '';

        // Find current user in the admin list
        const currentUserIndex = this.assignableAdmins.findIndex(
          (admin) => admin.name === currentUserName || admin.id === currentUserId
        );

        if (currentUserIndex !== -1) {
          // Move current user to the top
          const sortedAdmins = [...this.assignableAdmins];
          const currentUser = sortedAdmins.splice(currentUserIndex, 1)[0];
          return [currentUser, ...sortedAdmins];
        }
      }
    } catch (error) {
      console.warn('Could not get current user for admin dropdown:', error);
    }

    // Return original list if no current user found or error occurred
    return [...this.assignableAdmins];
  }

  private filterAdmins() {
    const searchControl = this.externalSearchControl || this.searchControl;
    const searchValue = searchControl.value?.toLowerCase() || '';
    const sortedAdmins = this.getSortedAdmins();
    this.filteredAdmins = sortedAdmins.filter((admin) => admin.name?.toLowerCase().includes(searchValue));
  }

  onSelectionChange(newValue: string | null) {
    this.value = newValue;
    this.onChange(newValue);
    this.onTouched();
    this.adminChange.emit(newValue);
  }

  onOpenedChange(opened: boolean) {
    if (opened && this.showSearch) {
      this.filterAdmins();
    }
    this.opened.emit(opened);
  }

  compareWith(a: string | null, b: string | null): boolean {
    return a === b;
  }

  // ControlValueAccessor methods
  writeValue(value: string | null): void {
    this.value = value;
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /**
   * Get the display value for the current selection
   */
  getDisplayValue(): string {
    if (this.value === null || this.value === undefined || this.value === '' || this.value === 'unassigned') {
      return 'Unassigned';
    }
    if (this.value === 'assigned') {
      return 'Assigned';
    }
    const admin = this.assignableAdmins.find((a) => a.id === this.value);
    return admin ? admin.name : 'Unassigned';
  }

  /**
   * Get the current selection value (useful for form integration)
   */
  getCurrentValue(): string | null {
    if (this.value === null || this.value === undefined || this.value === '' || this.value === 'unassigned') {
      return null;
    }
    if (this.value === 'assigned') {
      return 'assigned';
    }
    return this.value;
  }
}
